import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'primereact/button';
import { toast } from 'react-toastify';
import { Tooltip } from '@mui/material';
import './sportItemCard.css';
import {
  INextGamesInfo,
  ISportUserPreferences
} from '../../../../../types/sports/sportsInterfaces';
import {
  useDeleteSportUserPreferencesMutation,
  useSetSportUserPreferencesMutation
} from '../../../../../services/sportService/sportService';

const moveImg = require('../../../../../assets/move.png');

interface SportItemCardProps {
  preference: ISportUserPreferences;
  locationId: number;
  nextGame: INextGamesInfo;
  disableControls: boolean;
  reloadTable: () => void;
  toggleControls: (value: boolean) => void;
}

export const SportItemCard = ({
  preference,
  locationId,
  nextGame,
  disableControls,
  reloadTable,
  toggleControls
}: SportItemCardProps) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const navigate = useNavigate();

  const [
    deleteSportUserPreferences,
    { isLoading: isDeleting, isSuccess: successDelete }
  ] = useDeleteSportUserPreferencesMutation();

  const [
    setSportUserPreferences,
    { isLoading: isSetting, isSuccess: successSetting }
  ] = useSetSportUserPreferencesMutation();

  const goToTroubleshooter = () => {
    navigate(`/Locations/${locationId}/sport-troubleshooting`, {
      state: { team: preference.teamId, teamName: preference.teamName }
    });
  };

  const deletePreference = () => {
    toggleControls(true);
    const confirmed = window.confirm(
      'This operation will recalculate game schedules collisions. Proceed?'
    );
    if (!confirmed) {
      toggleControls(false);
      return;
    }

    deleteSportUserPreferences(preference.id);
  };

  useEffect(() => {
    if (successDelete) {
      toast.success('Preference deleted successfully.');
      reloadTable();
      toggleControls(false);
    }
  }, [successDelete]);

  const edit = () => {
    navigate(
      `/Locations/${locationId}/applications/game_day/${preference.id}/edit`
    );
  };

  const toggleEnabledSport = () => {
    const newPreference = { ...preference, enabled: !preference.enabled };
    toggleControls(true);
    setSportUserPreferences(newPreference);
  };

  useEffect(() => {
    if (successSetting) {
      toast.success('success toggle sport');
      toggleControls(false);
      reloadTable();
    }
  }, [successSetting]);

  const showInfo = () => {
    setIsCollapsed(!isCollapsed);
  };

  const editedByUser = () => {
    return preference.details.every((item: any) => !item.entityId);
  };

  const getTextForThreshold = (item: any) => {
    if (item.executeBeforeGame) {
      return 'Minutes before';
    }
    if (item.executeAfterGame) {
      return 'Minutes after';
    }
    return 'Threshold';
  };

  return (
    <div className="row" style={{ borderRadius: '5px' }}>
      <div className="col-12 card-container">
        <div className="title-header row">
          <div className="col-6 col-md-4 card-sport">
            <div
              className="drag-btn me-2 mr-2"
              style={{ display: 'inline-block' }}
            >
              <img src={moveImg} alt="move" className="move-img" />
            </div>
            <div className="info-container-sport">
              <h5>Team: {preference.teamName}</h5>
              <p>League: {preference.league}</p>
              <p>Priority: {preference.executionOrder}</p>
              {nextGame.date && (
                <p>
                  {`Next Game: ${new Date(nextGame.date).toLocaleString(
                    'en-US',
                    {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }
                  )}`}
                </p>
              )}
            </div>
          </div>
          <div className="col-12 col-md-4 text-md-end btns-container align-items-center d-flex justify-content-end">
            <InputSwitch
              checked={preference.enabled}
              tooltip={
                preference.enabled
                  ? 'Disable Sport Preference'
                  : 'Enable Sport Preference'
              }
              disabled={disableControls}
              onChange={toggleEnabledSport}
              className="switch-sport"
            />
            <Tooltip title="Show info">
              <button
                className="btn btn-secondary mx-1"
                type="button"
                disabled={disableControls}
                onClick={showInfo}
              >
                <i className="pi pi-search" />
              </button>
            </Tooltip>
            {editedByUser() ? (
              <Tooltip title="Needs to be configured">
                <button
                  className="btn btn-warning mx-1"
                  type="button"
                  onClick={edit}
                >
                  <i className="pi pi-cog" />
                </button>
              </Tooltip>
            ) : (
              <Tooltip title="Edit">
                <button
                  className="btn btn-secondary mx-1"
                  type="button"
                  disabled={disableControls}
                  onClick={edit}
                >
                  <i className="pi pi-cog" />
                </button>
              </Tooltip>
            )}
            <Tooltip title="Delete">
              <button
                className="btn btn-danger mx-1"
                type="button"
                disabled={disableControls}
                onClick={deletePreference}
              >
                <i className="pi pi-trash" />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>
      <div className="row info-container" style={{ paddingLeft: '40px' }}>
        {preference.details.map((item: any) => (
          <div
            key={item.id}
            className="info-card col-12 col-md-4"
            style={{
              display: isCollapsed ? 'none' : 'block'
            }}
          >
            <p className="title">*{item.preferenceName}:</p>
            <div className="text">
              <p className="bold">
                {item.entityType}:{' '}
                {item.entityId != null ? item.entityName : 'NONE'}
              </p>
              {(item.durationRequired || item.thresholdTimeRequired) &&
                item.entityId && (
                  <p className="bold">
                    {item.entityType === 'SCENE'
                      ? `${getTextForThreshold(item)}: ${item.thresholdMinutes}`
                      : `Duration: ${item.duration}s`}
                  </p>
                )}
              {item.channelConfigurations && item.entityType === 'EFFECT' && (
                <p className="bold">
                  Channels:{' '}
                  {item.channelConfigurations
                    .map((channel: any) => channel.name)
                    .join(', ')}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
